/**
 * NitroDrop Ranks & Permissions System
 * - Supports built-in and custom ranks, atomic CRUD with localStorage.
 * - Safe, robust, extensible, perfect for UI display & permission checks.
 */

const CUSTOM_RANK_KEY = 'nitroCustomRanks';

const PERMISSION_DEFAULTS = {
  canSendWorldMessages: true,
  canSendWorldImages: false,
  canSendPrivateImages: true
};

// Order/array for easier UI display and rank picking
export const RANK_ORDER = [
  'founder',
  'co-founder',
  'manager',
  'administrator',
  'head-admin',
  'admin',
  'mod',
  'partner',
  'advertiser',
  'sponsor',
  'system',
  'user'
];

export const DEFAULT_RANKS = {
  founder: {
    name: 'Founder',
    color: '#ffd700',
    permissions: { ...PERMISSION_DEFAULTS, canSendWorldImages: true } // Founders can send any images
  },
  'co-founder': {
    name: 'Co-Founder',
    color: '#ff9800',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  manager: {
    name: 'Manager',
    color: '#f44336',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  administrator: {
    name: 'Administrator',
    color: '#f44336',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  'head-admin': {
    name: 'Head Admin',
    color: '#f44336',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  admin: {
    name: 'Admin',
    color: '#2196f3',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  mod: {
    name: 'Moderator',
    color: '#2196f3',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  partner: {
    name: 'Partner',
    color: '#9c27b0',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  advertiser: {
    name: 'Advertiser',
    color: '#e91e63',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  sponsor: {
    name: 'Sponsor',
    color: '#4caf50',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  system: {
    name: 'System',
    color: '#6361e7',
    permissions: { ...PERMISSION_DEFAULTS }
  },
  user: {
    name: 'Member',
    color: '#aab1c7',
    permissions: { ...PERMISSION_DEFAULTS }
  }
};

/**
 * Safely parse custom ranks from localStorage (returns {} if not found or error)
 */
function parseCustomRanks() {
  try {
    const raw = localStorage.getItem(CUSTOM_RANK_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn('[rankConfig] Could not parse custom ranks:', err);
    return {};
  }
}

/**
 * Save custom ranks to localStorage (as a flat object, not array)
 */
function saveCustomRanks(ranks) {
  try {
    localStorage.setItem(CUSTOM_RANK_KEY, JSON.stringify(ranks));
  } catch (error) {
    console.error('[rankConfig] Custom rank save failed:', error);
  }
}

/**
 * Initializes the storage slot if needed (used by all APIs)
 */
export function ensureRankDefaults() {
  if (!localStorage.getItem(CUSTOM_RANK_KEY)) {
    saveCustomRanks({});
  }
}

/**
 * Returns merged rank map: { ...DEFAULT_RANKS, ...customRanks }
 * Custom ranks take precedence if there's an accidental collision.
 */
export function getAllRanks() {
  ensureRankDefaults();
  const custom = parseCustomRanks();
  return { ...DEFAULT_RANKS, ...custom };
}

/**
 * Gets rank object by ID (default: 'user'); always returns something valid for display
 * If the input role isn't found, falls back to 'user' default rank.
 */
export function getRankMeta(role = 'user') {
  const allRanks = getAllRanks();
  return allRanks[role] || DEFAULT_RANKS.user;
}

/**
 * Returns [{ id, ...meta, isDefault }, ...] for all ranks (custom + default)
 * Sorted in RANK_ORDER, then others alphabetically after.
 */
export function getRankList() {
  const ranks = getAllRanks();
  const allIds = Object.keys(ranks);
  // Maintain RANK_ORDER (default ranks) at the top, then custom/others alphabetically
  const sortedIds = [
    ...RANK_ORDER.filter(id => allIds.includes(id)),
    ...allIds.filter(id => !RANK_ORDER.includes(id)).sort()
  ];
  return sortedIds.map(id => ({
    id,
    ...ranks[id],
    isDefault: Object.prototype.hasOwnProperty.call(DEFAULT_RANKS, id)
  }));
}

/**
 * Converts arbitrary input to a safe ID. Letters+digits+dashes, lowercase, no dupes.
 */
// eslint-disable-next-line no-useless-escape
function sanitizeId(val) {
  if (!val) return '';
  return String(val)
    .toLowerCase()
    .replace(/\s+/g, '-')            // Replace whitespace with dash
    .replace(/[^a-z0-9-]/g, '')      // Remove all non-alphanum/dash chars (no \- needed)
    .replace(/-+/g, '-')             // Collapse multiple dashes (no \- needed)
    .replace(/^-+|-+$/g, '');        // Trim leading/trailing dash (no \- needed)
}

/**
 * Add or update a custom rank.
 * @param {Object} param
 * @param {string} param.id  - id for the rank (optional, else generated from name)
 * @param {string} param.name - display label for the rank (required)
 * @param {string} [param.color='#aab1c7']
 * @param {object} [param.permissions]
 * @returns rank meta (with id)
 */
export function upsertCustomRank({ id, name, color, permissions }) {
  ensureRankDefaults();
  const normalizedId = sanitizeId(id || name);
  if (!normalizedId) throw new Error('Rank requires a name or id');
  if (DEFAULT_RANKS[normalizedId]) throw new Error('Cannot override a default rank');
  const customRanks = parseCustomRanks();
  // Merge/default permissions, fallback to default
  customRanks[normalizedId] = {
    name: name || normalizedId,
    color: color || '#aab1c7',
    permissions: { ...PERMISSION_DEFAULTS, ...permissions }
  };
  saveCustomRanks(customRanks);
  return { id: normalizedId, ...customRanks[normalizedId] };
}

/**
 * Delete a custom rank by id or name. Throws if attempts to delete a default rank.
 */
export function deleteCustomRank(id) {
  ensureRankDefaults();
  const normalizedId = sanitizeId(id);
  if (DEFAULT_RANKS[normalizedId]) throw new Error('Cannot delete a default rank');
  const customRanks = parseCustomRanks();
  if (!(normalizedId in customRanks)) { return customRanks; } // no-op if absent
  delete customRanks[normalizedId];
  saveCustomRanks(customRanks);
  return customRanks;
}

/**
 * Check if a given rank id is a default or custom rank.
 * @returns 'default' | 'custom' | null
 */
export function rankType(id) {
  const safeId = sanitizeId(id);
  if (DEFAULT_RANKS[safeId]) return 'default';
  const customRanks = parseCustomRanks();
  if (customRanks[safeId]) return 'custom';
  return null;
}

/**
 * Utility for display: get color for a role id, with fallback.
 */
export function getRankColor(role) {
  const meta = getRankMeta(role);
  return (meta && meta.color) || '#aab1c7';
}
